var _ = require('underscore');
var i18nUtils = require('./../utils/i18nUtils');
var TorsoView = require('backbone-torso/modules/View');
var TorsoEvents = require('backbone-torso/modules/Events');
var demographicsSocialHistoryTemplate = require('./demographicsSocialHistoryTemplate.hbs');
var DemographicsValidationBehavior = require('./../behaviors/DemographicsValidationBehavior');

/**
 * View for displaying the social history information for patient demographics
 *
 * @class DemographicsSocialHistoryView
 * @constructor
 */
module.exports = TorsoView.extend({
  tagName: 'section',
  className: 'content-block',
  template: demographicsSocialHistoryTemplate,

  behaviors: {
    validationBehavior: {
      behavior: DemographicsValidationBehavior
    }
  },

  /**
   * @method initialize
   * @override
   * @param options {Object} configuration for this view
   *   @param options.dataModel {Object} contains all the data pertaining to social history
   *   @param options.config {Object} contains the configuration for social history information
   *   @param options.isInPreReg {boolean} whether it's in pre-reg or not
   *   @param options.editUrl {String} the url for corresponding edit view out of pre-reg
   *   @param options.preRegEditUrl {String} the url for corresponding edit view in pre-reg
   */
  initialize: function(options) {
    this._dataProperty = options.dataProperty || this._dataProperty;
    this._demographicsModel = options.dataModel || this._demographicsModel;
    this._config = options.config || this._config;
    this._isInPreReg = options.isInPreReg || this._isInPreReg;
    this._editUrl = options.editUrl || this._editUrl;
    this._preRegEditUrl = options.preRegEditUrl || this._preRegEditUrl;


    this.generateContext();
    this.listenTo(this._demographicsModel, 'change:' + this._dataProperty, this.generateContext);
    this.listenTo(this.viewState, 'change:context', this.render);
  },

  /**
   * @method _getConfig
   * @private
   * @param config the configuration for each fields
   * @param contextModel the model in context for each fields
   * @return the configuration in context for each fields
   */
  _getConfig: function(config, contextModel) {
    var contextConfig = {};
    contextConfig.disabled = config.fieldPermissions === 'DISABLED';
    contextConfig.hasData = !_.isEmpty(contextModel);
    contextConfig.required = config.validationConfiguration && config.validationConfiguration.required;
    contextConfig.missing = contextConfig.required && !contextConfig.hasData;
    return contextConfig;
  },

  /**
   * @method _getBooleanConfig
   * @private
   * @param config the configuration for boolean fields
   * @param contextModel the model in context for boolean fields
   * @returns the configuration in context for boolean fields
   */
  _getBooleanConfig: function(config, contextModel) {
    var contextConfig = this._getConfig(config, contextModel);
    contextConfig.hasData = _.isBoolean(contextModel);
    contextConfig.missing = contextConfig.required && !contextConfig.hasData;
    return contextConfig;
  },

  /**
   * Calculate the context based on data and configuration and set it on the view state
   * @method generateContext
   */
  generateContext: function() {
    var context = TorsoView.prototype.prepare.apply(this);

    context.model = _.clone(this._demographicsModel.get(this._dataProperty));
    context.editUrl = this._isInPreReg ? this._preRegEditUrl : this._editUrl;
    context.config = {};

    context.config.speakEnglish = this._getBooleanConfig(this._config.speakEnglish, context.model.speakEnglish);
    context.config.primaryLanguage = this._getConfig(this._config.primaryLanguage, context.model.primaryLanguage);
    context.config.preferredLanguage = this._getConfig(this._config.preferredLanguage, context.model.preferredLanguage);
    context.config.needInterpreter = this._getBooleanConfig(this._config.needInterpreter, context.model.needInterpreter);
    context.config.needAssistance = this._getBooleanConfig(this._config.needAssistance, context.model.needAssistance);
    context.config.typeOfAssistance = this._getConfig(this._config.typeOfAssistance, context.model.typeAssistance);
    context.config.ethnicity = this._getConfig(this._config.ethnicity, context.model.ethnicity);
    context.config.race = this._getConfig(this._config.race, context.model.race);
    context.config.additionalRace = this._getConfig(this._config.additionalRace, context.model.additionalRace);
    context.config.maritalStatus = this._getConfig(this._config.maritalStatus, context.model.maritalStatus);
    context.config.religion = this._getConfig(this._config.religion, context.model.religion);
    context.config.congregation = this._getConfig(this._config.congregation, context.model.congregation);
    context.config.birthCountry = this._getConfig(this._config.birthCountry, context.model.birthCountry);
    context.config.birthCity = this._getConfig(this._config.birthCity, context.model.birthCity);
    context.config.birthState = this._getConfig(this._config.birthState, context.model.birthState);

    var hasData = false;
    _.each(context.config, function(config) {
      if (config.hasData) {
        hasData = true;
      }
    });
    context.config.noData = !this.hasRequiredFields() && !hasData;

    this._setBirthLocationOnContext(context);

    this.set('context', context);
    TorsoEvents.trigger('demographics:validate');
  },

  /**
   * @method _setBirthLocationOnContext
   * @private
   * @param context to set values on
   */
  _setBirthLocationOnContext: function(context) {
    if (context.config.noData || !context.config.birthCountry || !context.config.birthCountry.hasData) {
      return;
    }

    if (['USA','UNITED STATES OF AMERICA', 'US', 'UNITED STATES'].indexOf(context.model.birthCountry) === -1) {
      context.config.birthState.disabled = true;
      context.config.birthCity.disabled = true;
    }

    var birthCountry = context.model.birthCountry.replaceAll(' ', '_');
    if(i18nUtils.hasPropertyValue('countryOptionList.' + birthCountry)) {
      context.model.birthCountry = birthCountry;
      context.config.birthCountry.hasMessageKey = true;
    }

    if (!context.config.birthState || !context.config.birthState.hasData) {
      return;
    }
    var birthState = context.model.birthState.replaceAll(' ', '_');
    if (context.config.birthState && context.config.birthState.hasData && i18nUtils.hasPropertyValue('stateOptionList.' + birthState)) {
        context.model.birthState = birthState;
        context.config.birthState.hasMessageKey = true;
    }
  }

});
